/**
 * Ruecklauf aus der Projektablage (Plan Stufe 4.3).
 *
 * Wer eine angeforderte Unterlage oder eine Antwort auf eine Klaerungsfrage
 * bekommt, legt sie als Datei in der Projektablage ab. Hier stehen die
 * REGELN, nach denen die Checkliste und die Fragenliste daraus Vorschlaege
 * machen — ohne React und ohne Supabase (`rueckflussLogic.test.ts`):
 *
 *   - „Beleg fuer Unterlage X": der Titel (oder Dateiname / Beschreibung) des
 *     Dokuments nennt ein Katalogdokument — ueber `findeDokument` (Id, Name,
 *     Kurzform, Alias, normalisiert) oder als Teilstring einer Schreibweise
 *     der Zeile. Freie Zeilen (eigene, SiGe-Plan) treffen ueber ihren Namen.
 *   - „Antwort auf PL-…": der Code einer Frage steht im Titel, Dateinamen
 *     oder in der Beschreibung (`CODE_MUSTER`).
 *
 * ═══ WAS EIN VORSCHLAG IST — UND WAS NICHT ════════════════════════════════
 * Ein Vorschlag ist ein Knopf. NICHTS wird hier verknuepft, kein Status
 * gesetzt: das Verknuepfen eines Belegs hebt die Unterlage auf „liegt vor"
 * (`verknuepfeUnterlagenDokument`), und das bestaetigt der Mensch im Dialog;
 * den Status einer Frage waehlt er dort ebenfalls — beantwortet, teilweise
 * oder unveraendert. Eine Datei mit „PL-A-02" im Namen kann auch eine
 * Rueckfrage sein, keine Antwort.
 *
 * Nicht vorgeschlagen wird, was die Anwendung selbst abgelegt hat
 * (`erzeugt_aus` gesetzt — die Klaerungsliste nennt alle Codes und waere
 * sonst „Antwort" auf jede Frage), was anderen Modulen zugeordnet ist (nur
 * `planungsphase` oder ohne Modul), was admin-gesperrt ist (fuer die meisten
 * Leser nicht vorhanden, belegt also nichts — dieselbe Regel wie
 * `belegfaehigeDokumente`) und was schon verknuepft ist (Beleg an einer
 * Zeile, Verweis in einer Antwort, Beleg einer Antwort — `antwort_beleg_id`).
 */

import { findeDokument, normalisiere } from '../../services/mitgeltendeDokumente';
import { antwortWegAus } from './antwortQuelle';
import type { Planungsfrage, UnterlagenStatus } from './types';
import type { UnterlagenZeile } from './unterlagenLogic';

// ═══════════════════════════════════════════════════════════════════════════
// EINGANG
// ═══════════════════════════════════════════════════════════════════════════

/** Was der Ruecklauf von einem Dokument der Ablage wissen muss (Ausschnitt aus `ProjektDokument`). */
export interface RueckflussDokument {
    id: string;
    titel: string;
    dateiname?: string | null;
    beschreibung?: string | null;
    module?: readonly string[] | null;
    /** Von der Anwendung abgelegt — dann kein Ruecklauf. */
    erzeugt_aus?: string | null;
    nur_admin?: boolean;
}

/** Ausschnitt einer Checklistenzeile, so viel wie die Vorschlaege brauchen. */
export type RueckflussZeile = Pick<UnterlagenZeile, 'id' | 'name' | 'status' | 'dokument' | 'eintrag'>;

/** Ausschnitt einer Frage, so viel wie die Vorschlaege brauchen. Die Quelle
 *  (Nr. 190) ist optional: ohne sie gilt „noch nichts vermerkt". */
export type RueckflussFrage = Pick<Planungsfrage, 'id' | 'code' | 'antwort' | 'antwort_beleg_id' | 'antwort_weg'>;

/** Wo der Treffer stand — fuer die Begruendung am Knopf. */
export type Fundstelle = 'Titel' | 'Dateiname' | 'Beschreibung';

export interface BelegVorschlag {
    art: 'beleg';
    dokument: RueckflussDokument;
    zeileId: string;
    /** Name der Unterlage, wie die Zeile ihn traegt. */
    unterlage: string;
    /** Klartext, woran der Vorschlag haengt — der Klick soll begruendet sein. */
    grund: string;
}

export interface AntwortVorschlag {
    art: 'antwort';
    dokument: RueckflussDokument;
    frageId: string;
    code: string;
    grund: string;
}

export interface RueckflussVorschlaege {
    belege: BelegVorschlag[];
    antworten: AntwortVorschlag[];
}

// ═══════════════════════════════════════════════════════════════════════════
// KANDIDATEN
// ═══════════════════════════════════════════════════════════════════════════

const MODUL_PLANUNGSPHASE = 'planungsphase';

/**
 * Taugt das Dokument als Ruecklauf? Nur hochgeladene Dateien (kein
 * `erzeugt_aus`), die der Planungsphase zugeordnet sind oder keinem Modul,
 * und die nicht admin-gesperrt sind.
 */
export const istRueckflussKandidat = (d: RueckflussDokument | null | undefined): boolean => {
    if (!d || !d.id) return false;
    if (d.erzeugt_aus) return false;
    if (d.nur_admin === true) return false;
    const module = d.module || [];
    return module.length === 0 || module.includes(MODUL_PLANUNGSPHASE);
};

/** Dateiname ohne Endung — „Baugrundgutachten_2026.pdf" → „Baugrundgutachten_2026". */
const ohneEndung = (dateiname: string | null | undefined): string =>
    String(dateiname || '').replace(/\.[A-Za-z0-9]{1,5}$/, '');

/** Die drei Textfelder eines Dokuments in Lesereihenfolge, leere weggelassen. */
const fundstellen = (d: RueckflussDokument): { wo: Fundstelle; text: string }[] =>
    [
        { wo: 'Titel' as Fundstelle, text: String(d.titel || '') },
        { wo: 'Dateiname' as Fundstelle, text: ohneEndung(d.dateiname) },
        { wo: 'Beschreibung' as Fundstelle, text: String(d.beschreibung || '') },
    ].filter(f => f.text.trim());

// ═══════════════════════════════════════════════════════════════════════════
// BELEG FUER UNTERLAGE
// ═══════════════════════════════════════════════════════════════════════════

/** Zeilen, die noch einen Beleg aufnehmen koennen: nicht als „nicht
 *  erforderlich" entschieden und noch ohne Beleg. Eine Zeile mit Beleg
 *  bekommt keinen zweiten vorgeschlagen — den Austausch macht der Mensch
 *  in der Zeile selbst, mit Blick auf den alten Beleg. */
const nimmtBelegAn = (z: RueckflussZeile): boolean => {
    const status: UnterlagenStatus = z.status;
    if (status === 'nicht_erforderlich') return false;
    return !(z.eintrag && z.eintrag.dokument_id);
};

/** Mindestlaenge einer Schreibweise fuer den Teilstring-Abgleich — kuerzer
 *  traefe „Plan" in jedem zweiten Titel. Dieselbe Schwelle wie
 *  `stehtInMassnahmen` im Katalog. */
const MINDESTLAENGE = 4;

/**
 * Trifft eine Schreibweise der Zeile den Text? Name und Alias als Teilstring
 * (normalisiert, mit Leerzeichen eingerahmt — so trifft „Baugrundgutachten"
 * auch in „Baugrundgutachten_Mueller_2026", aber „Statik" nicht mitten in
 * „Prüfstatik"), die Kurzform nur als ganzes Wort — sonst steckte „GB" in
 * „Umgebung". Fuer freie Zeilen zaehlt nur der Name.
 */
const schreibweiseTrifft = (z: RueckflussZeile, textNorm: string): string | null => {
    if (!textNorm) return null;
    const eingerahmt = ` ${textNorm} `;
    const lang = z.dokument
        ? [z.dokument.name, ...(z.dokument.alias || [])]
        : [z.name];
    for (const s of lang) {
        const n = normalisiere(s);
        if (n.length >= MINDESTLAENGE && eingerahmt.includes(` ${n} `)) return s;
        // Teilstring ohne Wortgrenze — nur bei laengeren Schreibweisen, die
        // kaum zufaellig in einem anderen Wort stecken.
        if (n.length >= 10 && eingerahmt.includes(n)) return s;
    }
    const kurz = z.dokument && z.dokument.kurz ? normalisiere(z.dokument.kurz) : '';
    if (kurz && eingerahmt.includes(` ${kurz} `)) return z.dokument!.kurz!;
    return null;
};

const belegVorschlaegeFuer = (
    d: RueckflussDokument,
    zeilen: readonly RueckflussZeile[],
): BelegVorschlag[] => {
    const out: BelegVorschlag[] = [];
    const gesehen = new Set<string>();
    const merke = (z: RueckflussZeile, grund: string) => {
        if (gesehen.has(z.id)) return;
        gesehen.add(z.id);
        out.push({ art: 'beleg', dokument: d, zeileId: z.id, unterlage: z.name, grund });
    };
    const offen = zeilen.filter(nimmtBelegAn);
    for (const f of fundstellen(d)) {
        // Erst der Vertrag des Katalogs: ganzer Text = Id, Name, Kurzform oder Alias.
        const genau = findeDokument(f.text);
        if (genau) {
            offen.filter(z => z.id === genau.id).forEach(z => merke(z, `${f.wo} nennt „${genau.name}"`));
        }
        const norm = normalisiere(f.text);
        offen.forEach(z => {
            const treffer = schreibweiseTrifft(z, norm);
            if (treffer) merke(z, `${f.wo} enthält „${treffer}"`);
        });
    }
    return out;
};

// ═══════════════════════════════════════════════════════════════════════════
// ANTWORT AUF EINE FRAGE
// ═══════════════════════════════════════════════════════════════════════════

/** Fragecodes: `PL-A-02`, `PL-I1-03`, `PL-X-07`. Nur ganze Codes — ein
 *  `PL-A-011` ist keiner, `XPL-A-02` auch nicht. Der Unterstrich zaehlt als
 *  Trenner (`pl-a-02_antwort.pdf`), deshalb kein `\b`. Gross-/
 *  Kleinschreibung spielt keine Rolle, der Treffer wird in Grossbuchstaben
 *  geliefert. */
export const CODE_MUSTER = /(?<![A-Za-z0-9])PL-[A-Z]\d?-\d\d(?!\d)/gi;

/** Alle Fragecodes eines Textes, ohne Dubletten, in Lesereihenfolge. */
export const codesIm = (text: string | null | undefined): string[] => {
    const out: string[] = [];
    const roh = String(text || '');
    for (const m of roh.matchAll(CODE_MUSTER)) {
        const code = m[0].toUpperCase();
        if (!out.includes(code)) out.push(code);
    }
    return out;
};

/** Der Satz, der an die Antwort gehaengt wird — wortgetreu, damit sich eine
 *  schon verknuepfte Datei wiedererkennen laesst (keine Doppelvorschlaege). */
export const dokumentVerweis = (d: Pick<RueckflussDokument, 'titel' | 'dateiname'>): string =>
    `Dokument: ${String(d.titel || '').trim() || String(d.dateiname || '').trim() || 'ohne Titel'} (Ablage)`;

/** Steht der Verweis auf dieses Dokument schon in der Antwort? */
export const istSchonVerknuepft = (antwort: string | null | undefined, d: Pick<RueckflussDokument, 'titel' | 'dateiname'>): boolean =>
    String(antwort || '').includes(dokumentVerweis(d));

/**
 * Haengt den Verweis an die Antwort an — auf eigener Zeile, nie doppelt.
 * Die bisherige Antwort bleibt wortgetreu stehen.
 */
export const haengeDokumentAn = (antwort: string | null | undefined, d: Pick<RueckflussDokument, 'titel' | 'dateiname'>): string => {
    const alt = String(antwort || '');
    if (istSchonVerknuepft(alt, d)) return alt;
    const verweis = dokumentVerweis(d);
    const stamm = alt.replace(/\s+$/, '');
    return stamm ? `${stamm}\n${verweis}` : verweis;
};

/** Was der Mensch im Dialog zum Status waehlt. `unveraendert` ist die Vorgabe. */
export type AntwortStatusWahl = 'beantwortet' | 'teilbeantwortet' | 'unveraendert';

/**
 * Die Aenderung an der Frage, wenn ein Dokument als Antwort verknuepft
 * wird: Verweis anhaengen, Status nur, wenn ausdruecklich gewaehlt. Den
 * Zeitstempel `beantwortet_am` setzt der Aufrufer (`aendereFrage` im Modul)
 * — hier wird nichts still als geklaert gefuehrt.
 *
 * Quelle (Migration Nr. 190): das Dokument wird Beleg (`antwort_beleg_id`)
 * und der Weg „Unterlage" — beides nur, wo noch nichts steht. Der Verweis im
 * Antworttext bleibt daneben, damit die Antwort auch ohne die Spalten lesbar
 * ist und ein schon verknuepftes Dokument wiedererkannt wird.
 */
export const antwortMitDokument = (
    frage: RueckflussFrage,
    d: Pick<RueckflussDokument, 'id' | 'titel' | 'dateiname'>,
    wahl: AntwortStatusWahl,
): Partial<Planungsfrage> => {
    const aenderung: Partial<Planungsfrage> = { antwort: haengeDokumentAn(frage.antwort, d) };
    if (wahl === 'beantwortet' || wahl === 'teilbeantwortet') aenderung.status = wahl;
    const id = String(d.id || '').trim();
    if (id && !String(frage.antwort_beleg_id || '').trim()) aenderung.antwort_beleg_id = id;
    if (!antwortWegAus(frage.antwort_weg)) aenderung.antwort_weg = 'unterlage';
    return aenderung;
};

const antwortVorschlaegeFuer = (
    d: RueckflussDokument,
    fragen: readonly RueckflussFrage[],
): AntwortVorschlag[] => {
    const out: AntwortVorschlag[] = [];
    const gesehen = new Set<string>();
    for (const f of fundstellen(d)) {
        for (const code of codesIm(f.text)) {
            fragen
                .filter(fr => String(fr.code || '').toUpperCase() === code)
                .forEach(fr => {
                    if (gesehen.has(fr.id)) return;
                    if (istSchonVerknuepft(fr.antwort, d)) return;
                    // Schon als Beleg der Antwort vermerkt — auch wenn der
                    // Verweis im Antworttext inzwischen geaendert wurde.
                    if (fr.antwort_beleg_id && fr.antwort_beleg_id === d.id) return;
                    gesehen.add(fr.id);
                    out.push({ art: 'antwort', dokument: d, frageId: fr.id, code: fr.code, grund: `${f.wo} nennt ${code}` });
                });
        }
    }
    return out;
};

// ═══════════════════════════════════════════════════════════════════════════
// ALLES ZUSAMMEN
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Vorschlaege zu den Dokumenten der Projektablage.
 *
 * Ein Dokument, das schon als Beleg an einer Zeile haengt, wird fuer KEINE
 * Zeile mehr vorgeschlagen — es ist zugeordnet. Reihenfolge: Ablage-
 * Reihenfolge der Dokumente, je Dokument Zeilen- bzw. Fragenreihenfolge.
 */
export const rueckflussVorschlaege = (
    dokumente: readonly RueckflussDokument[],
    zeilen: readonly RueckflussZeile[],
    fragen: readonly RueckflussFrage[],
): RueckflussVorschlaege => {
    const verknuepft = new Set<string>();
    (zeilen || []).forEach(z => { if (z.eintrag && z.eintrag.dokument_id) verknuepft.add(z.eintrag.dokument_id); });
    const belege: BelegVorschlag[] = [];
    const antworten: AntwortVorschlag[] = [];
    (dokumente || []).filter(istRueckflussKandidat).forEach(d => {
        if (!verknuepft.has(d.id)) belege.push(...belegVorschlaegeFuer(d, zeilen || []));
        antworten.push(...antwortVorschlaegeFuer(d, fragen || []));
    });
    return { belege, antworten };
};
